import { Box, Page, VisuallyHidden } from '@zillow/constellation-10';

import { LoadingImage, LoadingParagraph } from '@zg-rentals/loading-components';

import NavigationSkeleton from '../NavigationSkeleton';

import { TOP_NAV_BAR_HEIGHT } from '../../constants';
import {
  bodyContainerRecipe,
  layoutContainerRecipe,
  verticalNavigationContainerRecipe,
} from '../AppLayoutContainer/styles';

import type React from 'react';

export type Props = {
  // matches AppLayoutContainer: left nav (default) or the horizontal header used on colandlord/renter-hub routes
  useLeftNav?: boolean;
  // draws the IA top bar above a logo-less left nav, as AppLayoutContainer does when the IA nav is on
  iaNavEnabled?: boolean;
  // matches AppLayoutContainer: no nav bones at all for chromeless pages
  hideNavigation?: boolean;
  // matches AppLayoutContainer: no mobile header bones inside the native app's webview
  isInMobileWebView?: boolean;
  // page skeleton for the content area; defaults to a heading and two paragraphs
  children?: React.ReactNode;
};

const NAV_ROWS = 8;

const DefaultContent = () => (
  <Page.Root css={{ width: '100%', flex: 1, backgroundColor: 'bg.screen.neutral' }}>
    <Page.Header>
      <LoadingImage isLoading loadingWidth="240px" loadingHeight="40px" alt="" css={{ flexShrink: 0 }} />
    </Page.Header>
    <Page.Content>
      <LoadingParagraph isLoading loadingWidth="70%">
        {null}
      </LoadingParagraph>
      <LoadingParagraph isLoading loadingWidth="50%">
        {null}
      </LoadingParagraph>
    </Page.Content>
  </Page.Root>
);

const AppShellLoadingState = ({
  useLeftNav = true,
  iaNavEnabled = false,
  hideNavigation = false,
  isInMobileWebView = false,
  children,
}: Props) => {
  const showNavigation = !hideNavigation;
  const showIaTopBar = showNavigation && useLeftNav && iaNavEnabled;
  const chromeTop = `calc(var(--mock-user-banner-offset, 0px) + ${showIaTopBar ? TOP_NAV_BAR_HEIGHT : 0}px)`;

  return (
    <Box data-testid="AppShellLoadingState" aria-busy="true" css={layoutContainerRecipe.raw({ isLeftNav: useLeftNav })}>
      <VisuallyHidden asChild>
        <output>Loading</output>
      </VisuallyHidden>

      <Box aria-hidden="true" css={{ display: 'contents' }}>
        {showNavigation && !isInMobileWebView && (
          <Box
            data-testid="AppShellLoadingStateMobileHeader"
            css={{
              display: { base: 'flex', xxl: 'none' },
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '400',
              backgroundColor: 'bg.neutral',
            }}
            style={{ height: '55px' }}
          >
            <LoadingImage isLoading loadingWidth="32px" loadingHeight="32px" alt="" css={{ flexShrink: 0 }} />
            <LoadingImage isLoading loadingWidth="120px" loadingHeight="24px" alt="" css={{ flexShrink: 0 }} />
            <Box style={{ width: '32px' }} />
          </Box>
        )}

        {showIaTopBar && (
          <Box
            css={{
              display: { base: 'none', xxl: 'flex' },
              position: 'fixed',
              left: 0,
              right: 0,
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingInline: '400',
              backgroundColor: 'bg.neutral',
              borderBottom: '1px solid {colors.border.muted}',
              zIndex: 'menu',
            }}
            style={{ top: 'var(--mock-user-banner-offset, 0px)', height: `${TOP_NAV_BAR_HEIGHT}px` }}
          >
            <Box css={{ marginInlineStart: '300' }}>
              <LoadingImage isLoading loadingWidth="200px" loadingHeight="24px" alt="" css={{ flexShrink: 0 }} />
            </Box>
            <Box css={{ display: 'flex', alignItems: 'center', gap: '400' }}>
              <LoadingImage isLoading loadingWidth="32px" loadingHeight="32px" alt="" css={{ flexShrink: 0 }} />
              <LoadingImage isLoading loadingWidth="32px" loadingHeight="32px" alt="" css={{ flexShrink: 0 }} />
              <LoadingImage isLoading loadingWidth="32px" loadingHeight="32px" alt="" css={{ flexShrink: 0 }} />
            </Box>
          </Box>
        )}

        {showNavigation &&
          (useLeftNav ? (
            <Box
              css={{
                ...verticalNavigationContainerRecipe.raw({ hideNavigation: false }),
                display: { base: 'none', xxl: 'flex' },
                flexDirection: 'column',
                padding: '200',
              }}
              style={{ top: chromeTop, height: `calc(100% - ${chromeTop})` }}
            >
              {!showIaTopBar && (
                <Box css={{ padding: '{spacing.looser} 0 {spacing.looser} {spacing.tighter}' }}>
                  <LoadingImage isLoading loadingWidth="200px" loadingHeight="24px" alt="" css={{ flexShrink: 0 }} />
                </Box>
              )}
              <Box
                asChild
                css={{
                  flex: 1,
                  listStyle: 'none',
                  margin: 0,
                  padding: '200',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '200',
                }}
              >
                <ul>
                  <NavigationSkeleton rows={NAV_ROWS} />
                </ul>
              </Box>
              {!showIaTopBar && (
                <Box css={{ display: 'flex', alignItems: 'center', gap: '200', padding: '200' }}>
                  <LoadingImage isLoading loadingWidth="32px" loadingHeight="32px" alt="" css={{ flexShrink: 0 }} />
                  <LoadingImage isLoading loadingWidth="120px" loadingHeight="16px" alt="" css={{ flexShrink: 0 }} />
                </Box>
              )}
            </Box>
          ) : (
            <Box
              css={{
                display: { base: 'none', xxl: 'flex' },
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                paddingInline: '400',
                backgroundColor: 'bg.neutral',
                borderBottom: '1px solid {colors.border.muted}',
              }}
              style={{ height: '81px' }}
            >
              <LoadingImage isLoading loadingWidth="240px" loadingHeight="16px" alt="" css={{ flexShrink: 0 }} />
              <LoadingImage isLoading loadingWidth="154px" loadingHeight="32px" alt="" css={{ flexShrink: 0 }} />
              <LoadingImage isLoading loadingWidth="32px" loadingHeight="32px" alt="" css={{ flexShrink: 0 }} />
            </Box>
          ))}

        <Box
          css={{
            ...bodyContainerRecipe.raw({ hideNavigation, isLeftNav: useLeftNav }),
            display: 'flex',
            flexDirection: 'column',
            ...(showIaTopBar ? { marginTop: { xxl: '1800' } } : {}),
          }}
        >
          <Box css={{ display: 'flex', flexDirection: 'column', flex: 1, width: '100%' }}>
            {children ?? <DefaultContent />}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AppShellLoadingState;
